import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Box, Card, CardContent, Typography, TextField, Button,
  FormControlLabel, Checkbox, FormControl, FormGroup,
  CircularProgress, alpha, InputAdornment, Tooltip, Zoom, IconButton,
  Avatar,
} from '@mui/material';
import {
  Save as SaveIcon, Assignment as AssignmentIcon,
  TaskAlt as TaskSidebarIcon,
  Title as TitleIcon, Description as DescriptionIcon,
  Link as LinkIcon,
  Business as BusinessIcon, People as PeopleIcon,
  Flag as FlagIcon, Delete as DeleteIcon,
  Add as AddIcon, CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckOutlineIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import FeedbackMessage from '../../common/FeedbackMessage';
import api from '../../../services/api';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import {
  applyApiValidationErrors,
  createFieldErrorGate,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
} from '../../../utils/formValidationFeedback';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import CommonSectionCard from '../../common/SectionCard';
import SearchableSelect from '../../common/SearchableSelect';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canCreateTask,
  canEditTask,
  canAssigneeEditTask,
  engineeringHodDepartmentId,
  isEngineeringHodScoped,
} from '../../../utils/taskPermissions';
import { TASK_STATUS_FORM_OPTIONS, resolveTaskStatus, getTaskStatusMeta } from '../../../utils/taskStatus';

const TASK_VALIDATION_FIELDS = new Set([
  'title', 'description', 'status', 'documentation_link', 'project_link',
  'user_ids', 'department_ids', 'milestones',
]);

const isValidHttpUrl = (value) => {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const B = {
  50:  '#EFF6FF', 100: '#DBEAFE', 200: '#BFDBFE', 300: '#93C5FD',
  400: '#60A5FA', 500: '#3B82F6', 600: '#2563EB', 700: '#1D4ED8',
  800: '#1E40AF', 900: '#1E3A8A', 950: '#172554',
};

const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse    { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in  { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping     { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }
  @keyframes ms-slide-in   { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
`;

const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

const StyledField = ({ sx, ...props }) => (
  <TextField
    {...props}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.9rem',
        color: alpha('#E2E8F0', 0.85),
        background: alpha(SURFACE_UP, 0.7),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '& fieldset': { borderColor: alpha(NEON, 0.18), transition: 'border-color 0.2s ease' },
        '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
        '&.Mui-focused': {
          boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`,
          animation: 'form-glow-in 0.3s ease',
        },
        '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
        '&.Mui-error fieldset': { borderColor: 'rgba(248, 113, 113, 0.75) !important' },
        '&.Mui-error:hover fieldset': { borderColor: '#F87171 !important' },
        '&.Mui-error.Mui-focused fieldset': {
          borderColor: '#EF4444 !important',
          borderWidth: '1.5px',
          boxShadow: `0 0 0 3px ${alpha('#EF4444', 0.12)}`,
        },
        '&.Mui-disabled': {
          opacity: 1,
          background: alpha(SURFACE_UP, 0.55),
          '& fieldset': { borderColor: alpha(NEON, 0.22) },
        },
      },
      '& .MuiInputBase-input': {
        color: alpha('#E2E8F0', 0.85),
        '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 },
      },
      '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: alpha('#E2E8F0', 0.78),
        color: alpha('#E2E8F0', 0.78),
      },
      '& .MuiInputLabel-root': {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.875rem',
        color: alpha(NEON, 0.45),
      },
      '& .MuiInputLabel-root.Mui-disabled': {
        color: alpha(NEON, 0.58),
      },
      '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
      '& .MuiInputLabel-root.Mui-error': { color: '#F87171 !important' },
      '& .MuiInputLabel-root.Mui-required::after': { color: '#EF4444', fontWeight: 700 },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
      '& .MuiOutlinedInput-root.Mui-disabled .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: alpha(NEON, 0.55),
      },
      '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
      '& .MuiFormHelperText-root': {
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.68rem',
        letterSpacing: 0.3,
        color: `${alpha('#E2E8F0', 0.78)} !important`,
        WebkitTextFillColor: `${alpha('#E2E8F0', 0.78)} !important`,
        opacity: '1 !important',
      },
      '& .MuiFormHelperText-root.Mui-disabled': {
        color: `${alpha(NEON, 0.82)} !important`,
        WebkitTextFillColor: `${alpha(NEON, 0.82)} !important`,
        opacity: '1 !important',
      },
      '& .MuiFormHelperText-root.Mui-error': {
        color: '#F87171 !important',
        WebkitTextFillColor: '#F87171 !important',
      },
      '& .MuiOutlinedInput-root .MuiSelect-select': { color: alpha('#E2E8F0', 0.85) },
      ...sx,
    }}
  />
);

// ─── Main Component ───────────────────────────────────────────────────────────
const TaskForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { user: currentUser, loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    documentation_link: '',
    project_link: '',
    milestones: [],
    department_ids: [],
    user_ids: [],
    status: 'not_started',
  });

  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [newMilestone, setNewMilestone] = useState('');
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);
  const [editingMilestoneText, setEditingMilestoneText] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [pendingUpdateRequest, setPendingUpdateRequest] = useState(null);
  const [forbiddenEdit, setForbiddenEdit] = useState(false);
  const [loadedTask, setLoadedTask] = useState(null);

  const dragSrcRef = useRef(null);
  const navigate = useNavigate();

  const isAssigneeLimited = isEditMode && loadedTask && canAssigneeEditTask(currentUser, loadedTask);

  const hodDeptId = engineeringHodDepartmentId(currentUser);
  const hodScoped  = isEngineeringHodScoped(currentUser);

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'task',
    userId: currentUser?.id,
    isEditMode,
    enabled: !dataLoading && !authLoading,
    formData,
    setFormData,
  });

  const filteredDepartments = useMemo(() => {
    if (!hodScoped) return departments;
    return departments.filter((d) => Number(d.id) === hodDeptId);
  }, [departments, hodScoped, hodDeptId]);

  const filteredUsers = useMemo(() => {
    if (!hodScoped) return users;
    return users.filter((u) => Number(u.department_id) === hodDeptId);
  }, [users, hodScoped, hodDeptId]);

  const assignableUsers = useMemo(() => {
    const selectedDeptIds = (formData.department_ids || []).map((id) => Number(id)).filter((id) => !Number.isNaN(id));
    if (selectedDeptIds.length === 0) return filteredUsers;
    return filteredUsers.filter((u) => selectedDeptIds.includes(Number(u.department_id)));
  }, [filteredUsers, formData.department_ids]);

  const statusOptions = useMemo(
    () => TASK_STATUS_FORM_OPTIONS.map((opt) => {
      const meta = getTaskStatusMeta(opt.value);
      return { value: opt.value, label: meta.label };
    }),
    [],
  );

  const departmentOptions = useMemo(
    () => filteredDepartments.map((d) => ({ value: d.id, label: d.name })),
    [filteredDepartments],
  );

  const userOptions = useMemo(
    () => assignableUsers.map((u) => ({ value: u.id, label: u.name })),
    [assignableUsers],
  );

  const handleDepartmentIdsChange = (nextDeptIds) => {
    const normalizedDeptIds = (Array.isArray(nextDeptIds) ? nextDeptIds : [nextDeptIds])
      .map((deptId) => Number(deptId))
      .filter((id) => !Number.isNaN(id));
    setFormData((prev) => {
      const nextUserIds = (prev.user_ids || []).filter((uid) => {
        const user = filteredUsers.find((u) => Number(u.id) === Number(uid));
        if (!user) return false;
        return normalizedDeptIds.includes(Number(user.department_id));
      });
      return { ...prev, department_ids: normalizedDeptIds, user_ids: nextUserIds };
    });
    if (error) setError(null);
  };

  useEffect(() => {
    const cssId = 'taskform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId;
      el.innerHTML = FORM_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !currentUser) return;
    fetchData();
  }, [authLoading, currentUser?.id, isEditMode, id]);

  // ── Milestone normalisers ──────────────────────────────────────────────────
  // Converts any raw API shape → { id, text, done }[]
  const normalizeMilestones = (raw) =>
    (Array.isArray(raw) ? raw : [])
      .map((m, i) =>
        typeof m === 'string'
          ? { id: Date.now() + i, text: m, done: false }
          : { id: m?.id ?? Date.now() + i, text: m?.text || '', done: Boolean(m?.done) }
      )
      .filter((m) => m.text);

  // Converts { id, text, done }[] → API milestone objects (preserves done state).
  const normalizeMilestonesForApi = (milestones) =>
    (Array.isArray(milestones) ? milestones : [])
      .map((m) => {
        const text = (typeof m === 'string' ? m : m?.text || '').trim();
        if (!text) return null;
        const done = typeof m === 'string' ? false : Boolean(m?.done);
        const payload = { text, done };
        if (done && currentUser?.id) {
          payload.completed_by_id = Number(currentUser.id);
          payload.completed_at = new Date().toISOString();
        }
        return payload;
      })
      .filter(Boolean);

  const buildUpdatePayload = (data) => {
    const apiMilestones = normalizeMilestonesForApi(data.milestones);
    if (isAssigneeLimited) {
      return {
        description: data.description,
        documentation_link: data.documentation_link || null,
        project_link: data.project_link || null,
        milestones: apiMilestones,
        status: data.status,
      };
    }
    return {
      ...data,
      milestones: apiMilestones,
      department_ids: Array.isArray(data.department_ids)
        ? data.department_ids.map((deptId) => Number(deptId)).filter((deptId) => !Number.isNaN(deptId))
        : [],
      user_ids: Array.isArray(data.user_ids)
        ? data.user_ids.map((userId) => Number(userId)).filter((userId) => !Number.isNaN(userId))
        : [],
    };
  };

  const fetchData = async () => {
    try {
      setDataLoading(true);
      setForbiddenEdit(false);
      const requests = [api.get('/departments?minimal=1'), api.get('/users?minimal=1&active_only=1')];
      if (isEditMode && id) requests.push(api.get(`/tasks/${id}`));
      const [departmentsRes, usersRes, taskRes] = await Promise.allSettled(requests);

      setDepartments(departmentsRes.status === 'fulfilled' ? departmentsRes.value.data : []);
      setUsers(usersRes.status === 'fulfilled' ? usersRes.value.data : []);

      if (isEditMode && id && taskRes?.status === 'fulfilled') {
        const task = taskRes.value.data;
        if (!canEditTask(currentUser, task)) { setForbiddenEdit(true); setLoadedTask(null); return; }
        setLoadedTask(task);
        setFormData({
          title: task.title || '',
          description: task.description || '',
          documentation_link: task.documentation_link || '',
          project_link: task.project_link || '',
          milestones: normalizeMilestones(task.milestones),
          department_ids: task.departments?.map(d => d.id) || [],
          user_ids: task.users?.map(u => u.id) || [],
          status: resolveTaskStatus(task),
        });
      } else if (isEditMode && id && taskRes?.status === 'rejected') {
        setLoadedTask(null);
        throw taskRes.reason;
      }
    } catch {
      setError('Failed to load form data');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading || !currentUser || isEditMode || !hodScoped || hodDeptId == null) return;
    setFormData((prev) => {
      if (prev.department_ids.length > 0) return prev;
      return { ...prev, department_ids: [hodDeptId] };
    });
  }, [authLoading, currentUser?.id, isEditMode, hodScoped, hodDeptId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (error) setError(null);
    if (validationErrors[name]) setValidationErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTaskApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, TASK_VALIDATION_FIELDS);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        if (!TASK_VALIDATION_FIELDS.has(key)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[key] = String(msg);
      });
    }
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const collectValidationErrors = () => {
    const errors = {};
    if (!isAssigneeLimited && !formData.title.trim()) errors.title = 'Title is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (!isAssigneeLimited && !(formData.user_ids || []).length) {
      errors.user_ids = 'Select at least one user';
    }
    const docLink = (formData.documentation_link || '').trim();
    const projectLink = (formData.project_link || '').trim();
    if (docLink && !isValidHttpUrl(docLink)) {
      errors.documentation_link = 'Must be a valid URL (include http:// or https://).';
    }
    if (projectLink && !isValidHttpUrl(projectLink)) {
      errors.project_link = 'Must be a valid URL (include http:// or https://).';
    }
    return errors;
  };

  const validateForm = () => {
    const errors = collectValidationErrors();
    setValidationErrors(errors);
    setShowFieldErrors(true);
    if (Object.keys(errors).length > 0) {
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setError(null);
    return true;
  };

  // ── Milestone handlers ────────────────────────────────────────────────────
  const handleAddMilestone = () => {
    const text = newMilestone.trim();
    if (!text) return;
    setFormData((prev) => ({
      ...prev,
      milestones: [...prev.milestones, { id: Date.now(), text, done: false }],
    }));
    setNewMilestone('');
  };

  const handleRemoveMilestone = (id) => {
    setFormData((prev) => ({
      ...prev,
      milestones: prev.milestones.filter((m) => m.id !== id),
    }));
    if (editingMilestoneId === id) setEditingMilestoneId(null);
  };

  const handleToggleMilestoneDone = (id) => {
    setFormData((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m) =>
        m.id === id ? { ...m, done: !m.done } : m
      ),
    }));
  };

  const handleStartEditMilestone = (m) => {
    setEditingMilestoneId(m.id);
    setEditingMilestoneText(m.text);
  };

  const handleSaveEditMilestone = (id) => {
    const text = editingMilestoneText.trim();
    if (text) {
      setFormData((prev) => ({
        ...prev,
        milestones: prev.milestones.map((m) =>
          m.id === id ? { ...m, text } : m
        ),
      }));
    }
    setEditingMilestoneId(null);
    setEditingMilestoneText('');
  };

  // Drag-to-reorder
  const handleDragStart = (idx) => { dragSrcRef.current = idx; };
  const handleDrop = (idx) => {
    const src = dragSrcRef.current;
    if (src === null || src === idx) return;
    setFormData((prev) => {
      const list = [...prev.milestones];
      const [moved] = list.splice(src, 1);
      list.splice(idx, 0, moved);
      return { ...prev, milestones: list };
    });
    dragSrcRef.current = null;
  };

  const confirmUpdate = async () => {
    const errors = collectValidationErrors();
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen,
        setPendingUpdateRequest,
        setShowFieldErrors,
      });
      return;
    }
    try {
      setLoading(true);
      await api.put(`/tasks/${id}`, pendingUpdateRequest);
      setSuccessMessage('Task updated successfully! ');
      try { localStorage.setItem('taskListView', 'table'); } catch { /* ignore */ }
      setTimeout(() => navigate('/tasks', { state: { refreshList: Date.now() } }), 600);
    } catch (err) {
      handleTaskApiError(err, 'Failed to update task');
    } finally {
      setLoading(false);
      setConfirmUpdateDialogOpen(false);
      setPendingUpdateRequest(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        setError(null);
        setValidationErrors({});
        setShowFieldErrors(false);
        setPendingUpdateRequest(buildUpdatePayload(formData));
        setConfirmUpdateDialogOpen(true);
        return;
      }
      if (!validateForm()) return;
      setLoading(true);
      await api.post('/tasks', buildUpdatePayload(formData));
      if (!isEditMode) formDraft.clearDraft();
      setSuccessMessage('Task created successfully! ');
      try { localStorage.setItem('taskListView', 'table'); } catch { /* ignore */ }
      setTimeout(() => navigate('/tasks', { state: { refreshList: Date.now() } }), 600);
    } catch (err) {
      handleTaskApiError(err, 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  // ── Derived milestone stats ───────────────────────────────────────────────
  const milestones = formData.milestones || [];
  const doneCount = milestones.filter((m) => m.done).length;
  const milestonePct = milestones.length > 0 ? Math.round((doneCount / milestones.length) * 100) : 0;

  // ── Guard renders ──────────────────────────────────────────────────────────
  if (authLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading..." size={56} variant="primary" />
    </Box>
  );
  if (!currentUser) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message="Please log in to continue." severity="error" onClose={() => {}} />
    </Container>
  );
  if (!isEditMode && !canCreateTask(currentUser)) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Header title="Create Task" subtitle="You do not have permission to create tasks" icon={TaskSidebarIcon} showBackButton onBackClick={() => navigate('/tasks')} />
      <FeedbackMessage message="Only the team leader or engineering department heads can create tasks." severity="error" onClose={() => {}} sx={{ mt: 2 }} />
    </Container>
  );
  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading task form" size={56} variant="primary" />
    </Box>
  );
  if (forbiddenEdit) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Header title="Edit Task" subtitle="Access denied" icon={TaskSidebarIcon} showBackButton onBackClick={() => navigate('/tasks')} />
      <FeedbackMessage message="You do not have permission to edit this task." severity="error" onClose={() => {}} sx={{ mt: 2 }} />
    </Container>
  );

  const canSubmit = Boolean(
    formData.description.trim()
    && (isAssigneeLimited || (formData.title.trim() && (formData.user_ids || []).length)),
  );
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title={isEditMode ? 'Edit Task' : 'Create New Task'}
        subtitle={
          isEditMode
            ? (isAssigneeLimited
              ? 'Update your work on this task (assignment changes require a lead)'
              : 'Update task details and assignments')
            : 'Add a new task to track progress and assign team members'
        }
        icon={TaskSidebarIcon}
        showBackButton
        onBackClick={() => navigate('/tasks')}
      />

      <FeedbackMessage
        message={successMessage} severity="success" onClose={() => setSuccessMessage('')}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`,
          border: `1px solid ${alpha(NEON3, 0.35)}`,
          boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
          color: alpha(NEON3, 0.95),
        }}
      />
      <FeedbackMessage
        message={error} severity="error" onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          color: '#fca5a5',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
        }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── Section 1: Details ── */}
          <SectionCard title="Task Details" icon={<AssignmentIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                <StyledField
                  fullWidth label="Task Title" name="title" value={formData.title}
                  onChange={handleChange}
                  error={fieldHasError('title')} helperText={
                    fieldHelper('title')
                    || (isAssigneeLimited ? 'Only team leader or department head can change the title.' : undefined)
                  }
                  required={!isAssigneeLimited}
                  disabled={isAssigneeLimited}
                  InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon /></InputAdornment> }}
                  sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                />
                <SearchableSelect
                  label="Board Status"
                  value={formData.status}
                  onChange={(v) => {
                    setFormData((prev) => ({ ...prev, status: v }));
                    if (error) setError(null);
                  }}
                  options={statusOptions}
                  startIcon={<FlagIcon />}
                  sx={{ minWidth: { md: 220 }, flex: 1 }}
                />
              </Box>

              <StyledField
                fullWidth label="Description" name="description" value={formData.description}
                onChange={handleChange}
                error={fieldHasError('description')} helperText={fieldHelper('description')}
                required multiline rows={3}
                InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon sx={{ alignSelf: 'flex-start', mt: 1 }} /></InputAdornment> }}
                sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
              />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                <StyledField
                  fullWidth label="Documentation Link" name="documentation_link"
                  value={formData.documentation_link} onChange={handleChange}
                  placeholder="https://..."
                  error={fieldHasError('documentation_link')}
                  helperText={fieldHelper('documentation_link')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon /></InputAdornment> }}
                />
                <StyledField
                  fullWidth label="Project Link" name="project_link"
                  value={formData.project_link} onChange={handleChange}
                  placeholder="https://..."
                  error={fieldHasError('project_link')}
                  helperText={fieldHelper('project_link')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon /></InputAdornment> }}
                />
              </Box>
            </Box>
          </SectionCard>

          {/* ── Section 2: Milestones & Assignments ── */}
          <SectionCard title="Milestones & Assignments" icon={<PeopleIcon />} delay={0.08}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* ── Milestones header row ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FlagIcon sx={{ color: alpha(NEON, 0.55), fontSize: 17 }} />
                <Typography sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: alpha(NEON, 0.55),
                }}>
                  Milestones
                </Typography>
                {milestones.length > 0 && (
                  <Box sx={{
                    px: 1.2, py: 0.2,
                    borderRadius: '20px',
                    background: alpha(NEON, 0.1),
                    border: `1px solid ${alpha(NEON, 0.25)}`,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.65rem',
                    color: NEON,
                  }}>
                    {milestones.length}
                  </Box>
                )}
              </Box>

              {/* ── Add milestone input ── */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <StyledField
                  fullWidth
                  label="New milestone"
                  value={newMilestone}
                  onChange={(e) => setNewMilestone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleAddMilestone(); }
                  }}
                  placeholder="Describe a milestone…"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><FlagIcon /></InputAdornment>
                    ),
                  }}
                />
                <Button
                  onClick={handleAddMilestone}
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: '10px',
                    background: `linear-gradient(135deg, ${alpha(NEON, 0.22)}, ${alpha(B[700], 0.55)})`,
                    border: `1px solid ${alpha(NEON, 0.4)}`,
                    color: NEON,
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${alpha(NEON, 0.32)}, ${alpha(B[600], 0.65)})`,
                      boxShadow: `0 0 14px ${alpha(NEON, 0.3)}`,
                    },
                  }}
                >
                  Add
                </Button>
              </Box>

              {/* ── Progress bar ── */}
              {milestones.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    flex: 1,
                    height: '4px',
                    borderRadius: '2px',
                    background: alpha(NEON, 0.1),
                    overflow: 'hidden',
                  }}>
                    <Box sx={{
                      height: '100%',
                      width: `${milestonePct}%`,
                      background: NEON3,
                      borderRadius: '2px',
                      transition: 'width 0.4s ease',
                    }} />
                  </Box>
                  <Typography sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.68rem',
                    color: alpha(NEON3, 0.8),
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.04em',
                  }}>
                    {doneCount} / {milestones.length} done
                  </Typography>
                </Box>
              )}

              {/* ── Milestone list ── */}
              {milestones.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 3,
                  borderRadius: '10px',
                  border: `1px dashed ${alpha(NEON, 0.15)}`,
                  background: alpha(SURFACE_UP, 0.3),
                }}>
                  <FlagIcon sx={{ color: alpha(NEON, 0.2), fontSize: 28, mb: 0.5 }} />
                  <Typography sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.72rem',
                    color: alpha(NEON, 0.3),
                    letterSpacing: '0.05em',
                  }}>
                    No milestones yet — add one above
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {milestones.map((milestone, idx) => {
                    const isEditing = editingMilestoneId === milestone.id;
                    return (
                      <Box
                        key={milestone.id}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleDrop(idx)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.2,
                          px: 1.5,
                          py: 1.2,
                          borderRadius: '10px',
                          border: `1px solid ${milestone.done ? alpha(NEON3, 0.25) : alpha(NEON, 0.14)}`,
                          background: milestone.done
                            ? alpha(NEON3, 0.04)
                            : alpha(SURFACE_MID, 0.6),
                          cursor: 'grab',
                          animation: 'ms-slide-in 0.2s ease',
                          transition: 'border-color 0.2s, background 0.2s',
                          '&:hover': {
                            borderColor: milestone.done ? alpha(NEON3, 0.4) : alpha(NEON, 0.3),
                            background: milestone.done
                              ? alpha(NEON3, 0.07)
                              : alpha(SURFACE_MID, 0.9),
                          },
                          '&:active': { cursor: 'grabbing' },
                        }}
                      >
                        {/* Drag handle */}
                        <DragIcon sx={{
                          fontSize: 18,
                          color: alpha(NEON, 0.25),
                          flexShrink: 0,
                          cursor: 'grab',
                        }} />

                        {/* Done toggle */}
                        <Tooltip
                          title={milestone.done ? 'Mark incomplete' : 'Mark complete'}
                          TransitionComponent={Zoom}
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleMilestoneDone(milestone.id)}
                            sx={{
                              p: 0,
                              width: 22,
                              height: 22,
                              flexShrink: 0,
                              color: milestone.done ? NEON3 : alpha(NEON, 0.35),
                              '&:hover': {
                                color: milestone.done ? alpha(NEON3, 0.7) : alpha(NEON, 0.7),
                              },
                            }}
                          >
                            {milestone.done
                              ? <CheckCircleIcon sx={{ fontSize: 20 }} />
                              : <CheckOutlineIcon sx={{ fontSize: 20 }} />
                            }
                          </IconButton>
                        </Tooltip>

                        {/* Text / inline edit */}
                        {isEditing ? (
                          <Box
                            component="input"
                            value={editingMilestoneText}
                            onChange={(e) => setEditingMilestoneText(e.target.value)}
                            onBlur={() => handleSaveEditMilestone(milestone.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEditMilestone(milestone.id);
                              if (e.key === 'Escape') {
                                setEditingMilestoneId(null);
                                setEditingMilestoneText('');
                              }
                            }}
                            autoFocus
                            sx={{
                              flex: 1,
                              background: alpha(SURFACE_UP, 0.9),
                              border: `1px solid ${alpha(NEON, 0.4)}`,
                              borderRadius: '6px',
                              px: 1,
                              py: 0.5,
                              fontSize: '0.875rem',
                              color: alpha('#E2E8F0', 0.9),
                              fontFamily: "'DM Sans', sans-serif",
                              outline: 'none',
                            }}
                          />
                        ) : (
                          <Typography sx={{
                            flex: 1,
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: '0.875rem',
                            color: milestone.done
                              ? alpha('#E2E8F0', 0.38)
                              : alpha('#E2E8F0', 0.85),
                            textDecoration: milestone.done ? 'line-through' : 'none',
                            lineHeight: 1.45,
                            transition: 'color 0.2s',
                          }}>
                            {milestone.text}
                          </Typography>
                        )}

                        {/* Edit + Delete buttons */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          {!isEditing && (
                            <Tooltip title="Edit" TransitionComponent={Zoom} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleStartEditMilestone(milestone)}
                                sx={{
                                  width: 26, height: 26,
                                  color: alpha(NEON, 0.4),
                                  '&:hover': {
                                    background: alpha(NEON, 0.1),
                                    color: NEON,
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete" TransitionComponent={Zoom} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveMilestone(milestone.id)}
                              sx={{
                                width: 26, height: 26,
                                color: alpha('#E2E8F0', 0.2),
                                '&:hover': {
                                  background: alpha('#EF4444', 0.1),
                                  color: '#F87171',
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* ── Departments & Users (unchanged) ── */}
              {isAssigneeLimited ? (
                <Box sx={{
                  px: 2, py: 1.5, borderRadius: '10px',
                  border: `1px solid ${alpha(NEON, 0.12)}`,
                  background: alpha(SURFACE_UP, 0.45),
                }}>
                  <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha(NEON, 0.45), mb: 1 }}>
                    Assignment (read-only)
                  </Typography>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: alpha('#E2E8F0', 0.75), lineHeight: 1.6, mb: 1 }}>
                    <strong>Departments:</strong>{' '}
                    {loadedTask?.departments?.length
                      ? loadedTask.departments.map((d) => d.name).join(', ')
                      : 'None'}
                  </Typography>

                  <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha(NEON2, 0.55), mb: 0.5 }}>
                    Assigned Team
                  </Typography>
                  {loadedTask?.users?.length ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {loadedTask.users.map((user) => {
                        const initials = user?.name
                          ? user.name.split(' ').map((name) => name[0]).join('').slice(0, 2).toUpperCase()
                          : '?';

                        return (
                          <Box
                            key={user.id}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9, px: 1.2, borderRadius: '9px' }}
                          >
                            <Avatar sx={{
                              width: 30, height: 30, fontSize: '0.7rem', fontWeight: 700,
                              background: alpha(NEON2, 0.15), border: `1px solid ${alpha(NEON2, 0.25)}`,
                              color: NEON2, fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
                            }}>
                              {initials}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.88rem', color: alpha('#E2E8F0', 0.85), fontFamily: "'DM Sans', sans-serif", fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {user.name}
                              </Typography>
                              {user.email && (
                                <Typography sx={{ fontSize: '0.72rem', color: alpha('#E2E8F0', 0.45), fontFamily: "'DM Sans', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {user.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: alpha('#E2E8F0', 0.5), px: 1.2, py: 0.9 }}>
                      No users assigned
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <SearchableSelect
                    multiple
                    label="Departments"
                    value={formData.department_ids}
                    onChange={handleDepartmentIdsChange}
                    options={departmentOptions}
                    startIcon={<BusinessIcon />}
                    stackedTags
                  />

                  <SearchableSelect
                    multiple
                    required
                    label="Assigned Users"
                    value={formData.user_ids}
                    onChange={(nextUserIds) => {
                      setFormData((prev) => ({ ...prev, user_ids: nextUserIds }));
                      if (error) setError(null);
                      if (showFieldErrors && validationErrors.user_ids) {
                        setValidationErrors((prev) => ({ ...prev, user_ids: '' }));
                      }
                    }}
                    options={userOptions}
                    error={fieldHasError('user_ids')}
                    startIcon={<PeopleIcon />}
                    placeholder={userOptions.length ? '' : 'No users available'}
                    disabled={userOptions.length === 0}
                    helperText={
                      fieldHelper('user_ids')
                      || (assignableUsers.length === 0
                        ? 'No users found for the selected department(s).'
                        : `${assignableUsers.length} user(s) available`)
                    }
                    stackedTags
                    sx={{
                      '& .MuiFormLabel-asterisk': { color: 'red' },
                    }}
                  />
                </Box>
              )}
            </Box>
          </SectionCard>

          {/* ── Submit ── */}
          <SubmitActionsCard
            statusText={isEditMode ? 'Save changes to update this task.' : 'Complete the form to add a new task.'}
            missingRequiredFields={isEditMode ? null : [!isAssigneeLimited && !formData.title.trim() && 'Title', !formData.description.trim() && 'Description', !isAssigneeLimited && !(formData.user_ids || []).length && 'Assignee'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to create task."
            cancelLabel="Cancel"
            onCancel={() => navigate('/tasks')}
            cancelDisabled={loading}
            submitLabel={isEditMode ? 'UPDATE TASK' : 'CREATE TASK'}
            submitDisabled={loading || !canSubmit}
            loading={loading}
            submitStartIcon={<SaveIcon />}
            showSubmit
            submitType="submit"
            {...submitDraftProps}
          />
        </Box>
      </form>

      <ConfirmUpdateDialog
        open={confirmUpdateDialogOpen}
        onClose={() => { setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }}
        variant="edit"
        itemType="task"
        itemName={formData.title}
        onConfirm={confirmUpdate}
        title="Confirm Update"
        description="This action will update the task details and cannot be undone."
        confirmLabel="UPDATE TASK"
        loading={loading}
        confirmDisabled={!canSubmit}
      />
    </Container>
  );
};

export default TaskForm;
